import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import userIcon from "../assets/icons/usercircle-Vector.svg";
import ModalTemplate from "./ModalWrapper";
import SignUp from "./SignUp";
import Login from "./Login";
import { Heart, LogOut, UserIcon, X } from "lucide-react";
import useUserStore from "../store/userStore";
import { toast } from "react-toastify";
import type { User } from "../store/userStore";
import ProfileMenuButton from "./ProfileMenuButton";
import NavbarBrand from "./NavbarBrand";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Navbar = () => {
  const [modalType, setModalType] = useState<"signup" | "login" | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);

  const { user, clearUser } = useUserStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isProfileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  // Prevent body scroll when profile panel is open
  useEffect(() => {
    if (isProfileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isProfileMenuOpen]);

  const handleLogout = () => {
    clearUser();
    setIsProfileMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      toast.success("با موفقیت خارج شدید", {
        className: "toast",
        hideProgressBar: true,
        position: "top-center",
      });
    }, 100);
  };

  const getUserAvatar = (user: User) => {
    const imagePath = user?.image;
    if (!imagePath) return userIcon;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  return (
    <>
    <nav className="site-container absolute top-0 left-0 z-20 h-[70px] overflow-visible py-2 desktop:top-2 desktop:left-1/2 desktop:h-16 desktop:-translate-x-1/2 desktop:rounded-full desktop:bg-transparent desktop:py-0">
        <div className="mx-auto h-full">
          <div className="relative flex h-full items-center justify-between text-white" dir="ltr">
            {/* Right side buttons */}
            <div className="flex items-center gap-[8px] text-sm font-myYekanMedium desktop:gap-[28px]" dir="rtl">
              {user ? (
                <div className="relative" ref={profileMenuRef}>
                  <ProfileMenuButton
                    avatarUrl={getUserAvatar(user)}
                    fallbackAvatar={userIcon}
                    isOpen={isProfileMenuOpen}
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  />
                </div>
              ) : (
                // <button
                //   className="bg-[#ffffff]/20 text-black font-myYekanMedium backdrop-blur-md border border-[0.6px] border-gray-400 px-3 py-3 rounded-xl flex items-center gap-1 text-xs"
                //   onClick={() => setModalType("login")}
                // >
                //   <img src={userIcon} alt="user" className="w-4 h-4" />
                //   ورود | ثبت نام
                // </button>
                                <button
                  className="bg-[#ffffff]/20 text-black font-myYekanMedium backdrop-blur-md border border-[0.6px] border-gray-400 px-3 py-3 rounded-xl flex items-center gap-1 text-xs"
                  onClick={() => setModalType("login")}
                >
                  ورود | ثبت نام
                </button>
              )}
            </div>

  
            {/* <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-4 text-[#222222] font-myYekanDemibold desktop:flex desktop:gap-[32px]" dir="rtl">
              <button onClick={() => navigate("/")}>خانه</button>
              <button onClick={() => navigate("/about")}>درباره ما</button>
              <button onClick={() => navigate("/contact")}>ارتباط با ما</button>
            </div> */}
 
            <div className="shrink-0">
              <NavbarBrand />
            </div>
          </div>
        </div>

        {/* Gradient separator */}
        {pathname !== "/" && (
          <div className="absolute bottom-0 left-0 h-[1px] w-full bg-gradient-to-r from-transparent via-white/30 to-transparent desktop:hidden" />
        )}
      </nav>

      {/* Profile Side Panel */}
      {isProfileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] transition-all duration-300"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsProfileMenuOpen(false)}
            className="absolute top-4 left-4 z-[101] flex items-center justify-center w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Side Panel */}
          <div
            ref={profileMenuRef}
            className="absolute top-0 right-0 h-full w-[300px] bg-white shadow-2xl rounded-l-2xl overflow-y-auto"
          >
            {/* Header */}
            <div className="px-4 py-6 flex items-center gap-3 border-b border-gray-100">
              <img
                src={getUserAvatar(user!)}
                alt="avatar"
                className="w-14 h-14 rounded-full object-cover border-2 border-amber-500"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = userIcon;
                }}
              />
              <div>
                <p className="text-base font-bold text-gray-800">
                  {user?.fullName || user?.username || "کاربر"}
                </p>
                {user?.email && (
                  <p className="text-xs text-gray-500">{user?.email}</p>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2 font-myYekanRegular">
              <button
                onClick={() => { navigate("/userProfile"); setIsProfileMenuOpen(false); }}
                className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <UserIcon size={18} className="text-gray-500" />
                پروفایل من
              </button>

              <button
                onClick={() => { navigate("/userProfile?tab=saved"); setIsProfileMenuOpen(false); }}
                className="w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <Heart size={18} className="text-gray-500" />
                علاقه‌مندی‌ها
              </button>

              <div className="h-px bg-gray-100 my-2"></div>

              <button
                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                className="w-full text-right px-4 py-3 text-sm text-red-600 font-myYekanRegular hover:bg-red-50 flex items-center gap-3 transition-colors"
              >
                <LogOut size={18} className="text-red-500" />
                خروج از حساب
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for login/signup */}
      {modalType && (
        <ModalTemplate
          showModal={true}
          mobileBottomSheet
          className="!w-full !bg-white !px-0 !py-0 tablet:!w-[340px] tablet:!rounded-3xl tablet:!border tablet:!border-gray-200 tablet:!shadow-main desktop:!w-[440px]"
          onClose={() => setModalType(null)}
          mainComponent={
            modalType === "login" ? (
              <Login
                onClose={() => setModalType(null)}
                switchToSignup={() => setModalType("signup")}
              />
            ) : (
              <SignUp
                onClose={() => setModalType(null)}
                switchToLogin={() => setModalType("login")}
              />
            )
          }
        />
      )}
    </>
  );
};

export default Navbar;
